import axios from 'axios'
import { Message } from 'element-ui'
import { getToken, setToken, getRefreshToken, setRefreshToken, removeToken, removeRefreshToken } from './auth'
import { refreshToken } from '@/api/auth'
import router from '@/router'

const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '',
  timeout: 10000
})

// 是否正在刷新token
let isRefreshing = false
// 等待刷新token的队列
let refreshSubscribers = []

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach(({ resolve, config }) => {
    config.headers = config.headers || {}
    config.headers.Authorization = 'Bearer ' + newToken
    resolve(service(config))
  })
  refreshSubscribers = []
}

function onTokenRefreshFailed(error) {
  refreshSubscribers.forEach(({ reject }) => reject(error))
  refreshSubscribers = []
}

function addRefreshSubscriber(resolve, reject, config) {
  refreshSubscribers.push({ resolve, reject, config })
}

function redirectToLogin() {
  removeToken()
  removeRefreshToken()
  if (router.currentRoute.path !== '/login') {
    router.push('/login')
  }
}

service.interceptors.request.use(
  config => {
    const token = getToken()
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  response => {
    const res = response.data
    if (res.code !== 200) {
      // 423: 账号被锁定
      if (res.code === 423) {
        Message.error(res.message || '账号已被锁定')
        return Promise.reject(new Error(res.message || '账号已被锁定'))
      }
      // config.silent = true 的请求由调用方自己处理失败（如首页 KPI 允许降级为 0）
      if (!response.config || !response.config.silent) {
        Message.error(res.message || '请求失败')
      }
      if (res.code === 401) {
        redirectToLogin()
      }
      return Promise.reject(new Error(res.message || 'Error'))
    }
    return res
  },
  error => {
    const { config, response } = error

    // 处理401 token过期，尝试刷新
    if (response && response.status === 401 && !config._retry && config.url !== '/api/auth/refresh') {
      const refreshTokenValue = getRefreshToken()
      if (!refreshTokenValue) {
        Message.error('登录已过期，请重新登录')
        redirectToLogin()
        return Promise.reject(error)
      }

      if (!isRefreshing) {
        isRefreshing = true
        refreshToken({ refreshToken: refreshTokenValue }).then(res => {
          const { accessToken, refreshToken: newRefreshToken } = res.data
          setToken(accessToken)
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken)
          }
          isRefreshing = false
          onTokenRefreshed(accessToken)
        }).catch(refreshError => {
          isRefreshing = false
          Message.error('登录已过期，请重新登录')
          redirectToLogin()
          onTokenRefreshFailed(refreshError)
        })
      }

      // 将当前请求加入队列，等待token刷新后重试
      return new Promise((resolve, reject) => {
        config._retry = true
        addRefreshSubscriber(resolve, reject, config)
      })
    }

    // 兜底提示：区分「服务端有响应但没给 message」和「压根没收到响应」。
    // 否则代理 504 / 连接被重置 / 后端返回 HTML 错误页，都会显示成同一句
    // 「网络错误」，排查时完全看不出是哪一层出的问题。
    if (!(config && config.silent)) {
      if (!response) {
        Message.error(`网络异常：${error.message || '未收到响应'}（${config?.url || ''}）`)
      } else {
        Message.error(response.data?.message || `请求失败（HTTP ${response.status}）`)
      }
    }
    console.error('[request] 请求失败:', config?.method, config?.url, error)
    return Promise.reject(error)
  }
)

export default service
